/**
 * Holds the referral code the current person arrived with (if any) and owns the
 * PostHog captures for the referral funnel, so screens don't each re-implement
 * the analytics contract.
 *
 * Funnel (joined in PostHog on `code`):
 *   invite_friend_shared      (inviter taps Share)      -> { code, inviter_patient_id, ... }
 *   invite_friend_landed      (invitee opens the link)  -> { code } + first-touch person props
 *   referral_signup_completed (invitee finishes signup) -> { code }
 *
 * Attribution is carried by PostHog person properties (`referred_by_code`), set
 * `$set_once` at landing so it survives the anonymous -> identified transition
 * within a session. Cross-session / native-install attribution needs the
 * server-side redemption endpoint (see docs/features/PEER_INVITE_BACKEND.md).
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { usePostHog } from "posthog-react-native";
import { REFERRAL_CAMPAIGN_ID } from "./referralLink";

type ReferralContextValue = {
  /** The code this person landed with, or null if they arrived organically. */
  referralCode: string | null;
  /** Record that the person opened an invite link with this code. */
  captureLanding: (code: string) => void;
  /** Record a completed signup, attributed to the landing code if present. */
  captureSignupCompleted: () => void;
};

const ReferralContext = createContext<ReferralContextValue | null>(null);

export function ReferralProvider({ children }: { children: ReactNode }) {
  const posthog = usePostHog();
  const [referralCode, setReferralCode] = useState<string | null>(null);

  const captureLanding = useCallback(
    (code: string) => {
      if (!code) return;
      setReferralCode(code);
      posthog.capture("invite_friend_landed", {
        code,
        referral_campaign: REFERRAL_CAMPAIGN_ID,
        // First-touch attribution: only the first invite a person lands on
        // wins, and it persists onto their (eventually identified) profile.
        $set_once: {
          referred_by_code: code,
          referral_campaign: REFERRAL_CAMPAIGN_ID,
        },
      });
    },
    [posthog],
  );

  const captureSignupCompleted = useCallback(() => {
    if (!referralCode) return;
    posthog.capture("referral_signup_completed", {
      code: referralCode,
      referral_campaign: REFERRAL_CAMPAIGN_ID,
      $set: {
        referred_by_code: referralCode,
        referral_campaign: REFERRAL_CAMPAIGN_ID,
      },
    });
  }, [posthog, referralCode]);

  const value = useMemo(
    () => ({ referralCode, captureLanding, captureSignupCompleted }),
    [referralCode, captureLanding, captureSignupCompleted],
  );

  return (
    <ReferralContext.Provider value={value}>
      {children}
    </ReferralContext.Provider>
  );
}

export function useReferral(): ReferralContextValue {
  const ctx = useContext(ReferralContext);
  if (!ctx) {
    throw new Error("useReferral must be used within a ReferralProvider");
  }
  return ctx;
}
